import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { FileEntityResponseData } from '@/shared/types/fileTypes';
import { cn } from '@/shared/utils/date-utils';

interface ImagePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  images: FileEntityResponseData[];
  /** Image to show when the dialog opens. */
  imageId: string | null;
  title?: string | null;
}

function preloadImage(url: string, onLoaded?: (url: string) => void): void {
  const img = new Image();
  img.onload = () => onLoaded?.(url);
  img.onerror = () => undefined;
  img.src = url;
}

const ImagePreviewDialog = ({
  open,
  onOpenChange,
  images,
  imageId,
  title,
}: ImagePreviewDialogProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  /** Session cache — URLs already loaded this time the dialog is open. */
  const [seenUrls, setSeenUrls] = useState<Set<string>>(() => new Set());
  const imgRef = useRef<HTMLImageElement>(null);

  const markSeen = useCallback((url: string) => {
    setSeenUrls((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!open || !imageId) return;
    const index = images.findIndex((item) => item.fileId === imageId);
    if (index >= 0) setCurrentIndex(index);
  }, [open, imageId, images]);

  useEffect(() => {
    if (!open) {
      setSeenUrls(new Set());
      setIsLoading(true);
    }
  }, [open]);

  const image = images[currentIndex] ?? null;
  const imageUrl = image?.url ?? null;

  // Preload gallery + mark seen as each finishes loading.
  useEffect(() => {
    if (!open || images.length === 0) return;
    images.forEach(({ url }) => preloadImage(url, markSeen));
  }, [open, images, markSeen]);

  // Preload neighbors when navigating.
  useEffect(() => {
    if (!open) return;
    [currentIndex - 1, currentIndex + 1].forEach((index) => {
      const url = images[index]?.url;
      if (url && !seenUrls.has(url)) preloadImage(url, markSeen);
    });
  }, [open, currentIndex, images, seenUrls, markSeen]);

  // Counter updates immediately; skip loading UI for images already in session cache.
  useEffect(() => {
    if (!imageUrl) {
      setIsLoading(false);
      return;
    }

    if (seenUrls.has(imageUrl)) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const el = imgRef.current;
    if (el?.complete && el.naturalWidth > 0) {
      markSeen(imageUrl);
      setIsLoading(false);
    }
  }, [imageUrl, currentIndex, seenUrls, markSeen]);

  const showPrevious = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const showNext = () => {
    if (currentIndex < images.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const handleImageLoad = () => {
    if (imageUrl) markSeen(imageUrl);
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] gap-4 overflow-hidden p-4">
        <DialogHeader>
          <DialogTitle>{title ?? 'Image(s) Preview'}</DialogTitle>
        </DialogHeader>

        {image && (
          <div className="relative flex min-h-[50vh] items-center justify-center rounded-md bg-muted/30">
            {isLoading && (
              <div className="absolute inset-0 z-[1] flex flex-col items-center justify-center gap-2 bg-muted/40">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Loading image…</span>
              </div>
            )}

            {images.length > 1 && currentIndex > 0 && (
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute left-2 top-1/2 z-10 -translate-y-1/2 h-9 w-9 rounded-full shadow-md"
                onClick={showPrevious}
                aria-label="Previous image"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}

            <img
              ref={imgRef}
              key={image.fileId}
              src={image.url}
              alt="Image preview"
              className={cn(
                'max-h-[80vh] w-full object-contain transition-opacity duration-200',
                isLoading ? 'opacity-0' : 'opacity-100',
              )}
              decoding="async"
              fetchPriority="high"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />

            {images.length > 1 && currentIndex < images.length - 1 && (
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute right-2 top-1/2 z-10 -translate-y-1/2 h-9 w-9 rounded-full shadow-md"
                onClick={showNext}
                aria-label="Next image"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            )}
          </div>
        )}

        {image && images.length > 1 && (
          <p className="text-center text-xs text-muted-foreground">
            {currentIndex + 1} of {images.length}
            {isLoading ? ' · loading…' : ''}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImagePreviewDialog;
